// ---------------------------------------------------------------------------
// Demo / Test Mode account (CLE-251).
//
// A dedicated, frontend-only "demo" login that drops the user straight into
// Test Mode with the simulated Clean Code Culture dataset. It never touches the
// real backend: logging in synthesizes an admin session locally and flips the
// Test Mode flag ON. Logging out flips it back OFF.
//
// This is intentionally separate from the real auth flow so the two can never
// interfere — real logins always hit the backend (see lib/api.ts, which now
// routes /api/auth/* to the real server regardless of Test Mode), and the demo
// login is recognised purely by its fixed credentials / token sentinel.
// ---------------------------------------------------------------------------

import type { AuthSession, AuthUser } from './authApi';

/** Fixed credentials for the demo account. */
export const DEMO_EMAIL = 'test@cleancodeculture.com';
export const DEMO_PASSWORD = 'testmode123';

// Sentinel token used to recognise a persisted demo session on reload without
// calling the backend. Deliberately not a real JWT.
const DEMO_TOKEN = 'demo-mode-token';

const DEMO_USER: AuthUser = {
  id: 'demo-user',
  email: DEMO_EMAIL,
  name: 'Demo User',
  role: 'ADMIN',
  active: true,
  avatarUrl: null,
  lastLoginAt: null,
  createdAt: new Date(0).toISOString(),
};

/** True when the given credentials are the demo account's. */
export function isDemoCredentials(email: string, password: string): boolean {
  return email.trim().toLowerCase() === DEMO_EMAIL && password === DEMO_PASSWORD;
}

/** True when a persisted access token belongs to the demo session. */
export function isDemoToken(token: string | null | undefined): boolean {
  return token === DEMO_TOKEN;
}

/** Build the synthetic session returned by a demo login (no backend call). */
export function buildDemoSession(): AuthSession {
  return {
    user: { ...DEMO_USER },
    token: DEMO_TOKEN,
    accessToken: DEMO_TOKEN,
    refreshToken: DEMO_TOKEN,
  };
}
